import { LoaderFunctionArgs, Outlet } from 'react-router-dom';

import { TProjectViewerLoaderResponse } from '@/console/features/project-viewer/project-viewer.types';
import { TProjectAnalysisLoaderResponse } from '@/console/features/projects/components/project-analysis/project-analysis.types';
import {
  TCrumb,
  TCustomRouteObject,
  THandle,
} from '@/shared/types/router.types';
import { ProjectFragment } from '@/shared/types/types.generated';

const getProjectViewerAncestor = (
  data: TProjectViewerLoaderResponse,
): TCrumb | undefined => ({
  label: data.title,
  link: `projects/${data.assetId}`,
});

const getProjectAnalysisAncestor = (
  data: TProjectAnalysisLoaderResponse,
): TCrumb | undefined => ({
  label: data.project?.name,
  link: `projects/${data.project?.projectId}`,
});

export const projectInnerRoutes: TCustomRouteObject[] = [
  {
    path: ':projectId/:tabId?',
    lazy: async () => {
      const { ProjectRoute } = await import('./project');
      return { Component: ProjectRoute };
    },
    shouldRevalidate: () => false,
    loader: async (args: LoaderFunctionArgs) => {
      const { projectLoader } = await import('./project');
      return projectLoader(args);
    },
    handle: {
      crumb: {
        label: (data: ProjectFragment) => data.name,
      },
    },
  },
  {
    path: ':projectId/view/:versionId',
    lazy: async () => {
      const { ProjectViewerRoute } = await import('./project-viewer');
      return { Component: ProjectViewerRoute };
    },
    loader: async (args: LoaderFunctionArgs) => {
      const { projectViewerLoader } = await import('./project-viewer');
      return projectViewerLoader(args);
    },
    handle: {
      crumb: {
        label: 'view-project',
        localized: true,
        ancestor: getProjectViewerAncestor,
      },
    },
  },
  {
    path: ':projectId/compare/:baseVersionId/:targetVersionId',
    lazy: async () => {
      const { ProjectCompareRoute } = await import('./project-compare');
      return { Component: ProjectCompareRoute };
    },
    loader: async (args: LoaderFunctionArgs) => {
      const { projectCompareLoader } = await import('./project-compare');
      return projectCompareLoader(args);
    },
    handle: {
      crumb: {
        label: 'compare-versions',
        localized: true,
        ancestor: getProjectViewerAncestor,
      },
    },
  },
  {
    path: ':projectId/merge/:baseVersionId/:targetVersionId',
    lazy: async () => {
      const { ProjectMergeRoute } = await import('./project-merge');
      return { Component: ProjectMergeRoute };
    },
    loader: async (args: LoaderFunctionArgs) => {
      const { projectMergeLoader } = await import('./project-merge');
      return projectMergeLoader(args);
    },
    handle: {
      crumb: {
        label: 'merge-versions',
        localized: true,
        ancestor: getProjectViewerAncestor,
      },
    },
  },
  {
    path: ':projectId/analysis/:versionId',
    lazy: async () => {
      const { ProjectAnalysisRoute } = await import('./project-analysis');
      return { Component: ProjectAnalysisRoute };
    },
    loader: async (args: LoaderFunctionArgs) => {
      const { projectAnalysisLoader } = await import('./project-analysis');
      return projectAnalysisLoader(args);
    },
    handle: {
      crumb: {
        label: 'version-analysis',
        localized: true,
        ancestor: getProjectAnalysisAncestor,
      },
    },
  },
];

export const projectRouteHandle: THandle = {
  crumb: {
    label: 'projects',
    localized: true,
  },
  entity: 'Project',
};

export const projectRoutes: TCustomRouteObject = {
  path: '/projects',
  Component: () => <Outlet />,
  handle: projectRouteHandle,
  children: [
    {
      index: true,
      lazy: async () => {
        const { ProjectsListRoute } = await import('./projects-list');
        return { Component: ProjectsListRoute };
      },
    },
    ...projectInnerRoutes,
  ],
};
